import { ComponentInteractionType, ComponentOutput, SceneComponent } from './Scene.component';
import DeviceManager from '../../components/util/deviceManager';

enum LoadingState {
  Idle = 'Idle',
  Loaded = 'Loaded',
  Loading = 'Loading',
  Error = 'Error',
}

type Inputs = {
  src: MediaStream | string | HTMLVideoElement | null;
  muted: boolean | null;
  loop: boolean | null;
  autoplay: boolean | null;
  visible: boolean;
  playsInline: boolean;
  localRotation: {
    x: number;
    y: number;
    z: number;
  };
  skipRender: boolean;
  width: number;
  // useRadius: boolean;
};

type Outputs = {
  texture: any;
  loadingState: LoadingState;
} & ComponentOutput;

const DEFAULT_WIDTH = 1;
// const DEFAULT_RADIUS = 0.1;

export class VideoRenderer extends SceneComponent {
  private video: HTMLVideoElement;

  private texture: any;

  private root: any = null;

  private pivot: any = null;

  private plane: any = null;

  private backSidePlane: any = null;

  private playBtn: any = null;

  private muteBtn: any = null;

  private aspect = 1.77;

  private aspectHeight: number = DEFAULT_WIDTH / this.aspect;

  private hasPlayPermission = false;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  destroyButton = () => {};

  inputs: Inputs = {
    src: null, // src must be added after the node start!
    muted: true,
    loop: true,
    autoplay: true,
    visible: true,
    playsInline: true,
    localRotation: { x: 0, y: 0, z: 0 },
    skipRender: false,
    width: DEFAULT_WIDTH,
    // useRadius: false,
  };

  outputs = {
    texture: null,
    loadingState: LoadingState.Idle,
  } as Outputs;

  // setting which events are compatible with the component
  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false,
  };

  onInit() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();
    this.outputs.objectRoot = this.root;
    if (this.inputs.visible) {
      // setting the collider to add the click event
      this.outputs.collider = this.root;
    }
    this.root.add(this.pivot);
  }

  onInputsUpdated() {
    try {
      if (this.inputs.skipRender) {
        const { localRotation } = this.inputs;
        this.root.rotation.set(localRotation.x, localRotation.y, localRotation.z);

        return;
      }

      this.releaseTexture();

      const THREE = this.context.three;
      if (!this.inputs.src) {
        this.video.src = '';
        this.outputs.loadingState = LoadingState.Idle;
        return;
      }

      this.outputs.loadingState = LoadingState.Loading;

      if (this.inputs.src instanceof HTMLVideoElement) {
        this.video = this.inputs.src;
      } else {
        this.video = this.createVideoElement();

        if (typeof this.inputs.src === 'string') {
          this.video.src = this.inputs.src;
        } else {
          this.video.srcObject = this.inputs.src;
        }

        this.video.load();
      }

      const onCanPlay = () => {
        try {
          const videoElement: HTMLVideoElement & { webkitAudioDecodedByteCount?: number } & {
            mozHasAudio?: boolean;
          } & { audioTracks?: any[] } = this.video;
          const audioDataNotPresented =
            videoElement?.webkitAudioDecodedByteCount === 0 ||
            videoElement?.mozHasAudio === false ||
            videoElement?.audioTracks?.length === 0;
          if (audioDataNotPresented) {
            if (this.destroyButton && typeof this.destroyButton === 'function') {
              this.destroyButton();
            }
          }
          this.video.removeEventListener('canplaythrough', onCanPlay);

          const { videoWidth, videoHeight } = this.video;
          if (!videoWidth || !videoHeight) {
            // return console.debug('Unable to get video resolution.');
            throw new Error('Unable to get video resolution.');
          }
          this.aspect = videoWidth / videoHeight;
          this.aspectHeight = this.inputs.width / this.aspect;

          const geometry = new THREE.PlaneGeometry(this.inputs.width, this.aspectHeight);

          // TODO: finish with rounded rectangle
          // https://threejs.org/examples/#webgl_geometry_shapes - rounded rect shape
          // const geometry = this.makeRoundedRect(
          //   0,
          //   0,
          //   this.inputs.width,
          //   this.aspectHeight,
          //   this.inputs.useRadius ? DEFAULT_RADIUS : 0,
          // );

          this.texture = new THREE.VideoTexture(this.video);
          this.texture.encoding = THREE.sRGBEncoding;
          this.texture.minFilter = THREE.LinearFilter;
          this.texture.magFilter = THREE.LinearFilter;
          this.texture.format = THREE.RGBAFormat;
          this.texture.wrapS = THREE.ClampToEdgeWrapping; // THREE.RepeatWrapping
          this.texture.wrapT = THREE.ClampToEdgeWrapping; // THREE.RepeatWrapping
          this.texture.repeat.set(1, 1);

          const material = new THREE.MeshBasicMaterial({
            map: this.texture,
            blending: THREE.NormalBlending,
            transparent: true,
            visible: this.inputs.visible,
          });
          const backSideMaterial = new THREE.MeshBasicMaterial({
            side: THREE.DoubleSide,
            color: 0x000000,
            transparent: false,
            visible: this.inputs.visible,
            polygonOffset: true,
            polygonOffsetUnits: -4,
          });

          this.plane = new THREE.Mesh(geometry, material);
          this.pivot?.add(this.plane);
          // fixing plane the position
          // this.plane.position.set(
          //   -((this.inputs.width * this.aspect) / 2),
          //   -(this.aspectHeight / 2),
          //   0,
          // );
          // this.plane.scale.set(
          //   this.aspect,
          //   1,
          //   1,
          // );

          this.backSidePlane = new THREE.Mesh(geometry, backSideMaterial);
          this.backSidePlane.position.set(0, 0, -0.01);
          this.pivot?.add(this.backSidePlane);
          // fixing plane the position
          // this.backSidePlane.position.set(
          //   -((this.inputs.width * this.aspect) / 2),
          //   -(this.aspectHeight / 2),
          //   -0.01,
          // );

          const { localRotation } = this.inputs;
          this.root.rotation.set(localRotation.x, localRotation.y, localRotation.z);

          this.outputs.texture = this.texture;

          if (this.inputs.autoplay) {
            const interval = setInterval(async () => {
              try {
                await this.video.play();
                clearInterval(interval);
              } catch (err) {
                console.error(err);
              }
            }, 1000);
          }
        } catch (e) {
          console.error('[PUBLIC VideoRenderer]:', e);
          this.video.removeEventListener('canplaythrough', onCanPlay);
          this.outputs.loadingState = LoadingState.Error;
        }
      };
      const onError = () => {
        this.video.removeEventListener('error', onError);
        this.outputs.loadingState = LoadingState.Error;
      };

      this.video.addEventListener('canplaythrough', onCanPlay);
      this.video.addEventListener('error', onError);
      if (DeviceManager.isIOS()) {
        this.video.addEventListener(
          'pause',
          () => {
            // mute and play the video in case the browser stopped it
            // i.e. Safari stops the video if the other one was unmuted
            if (this.video.paused && this.hasPlayPermission) {
              this.toggleMute();
            }
          },
          false,
        );
      }
    } catch (e) {
      console.error('[VideoRenderer]:', e);
      this.outputs.loadingState = LoadingState.Error;
    }
  }

  onDestroy() {
    this.releaseTexture();
    if (this.playBtn) {
      this.playBtn.parent?.remove(this.playBtn);
      this.playBtn.material.dispose();
      this.playBtn.geometry.dispose();
      this.playBtn = null;
    }
    if (this.muteBtn) {
      this.muteBtn.parent?.remove(this.muteBtn);
      this.muteBtn.material.dispose();
      this.muteBtn.geometry.dispose();
      this.muteBtn = null;
    }
  }

  isMuted() {
    return this.video.muted;
  }

  toggleMute(muted?: boolean) {
    const value = typeof muted === 'boolean' ? muted : !this.video.muted;
    if (!this.video) return;

    this.video.muted = value;

    if (DeviceManager.isIOS() && !value && !this.hasPlayPermission) {
      // Safari support the autoplay unmute only after user interacts with Treedis website (not iframe)
      // https://developer.apple.com/documentation/webkit/delivering_video_content_for_safari#3030259
      const overlay = document.createElement('div');
      overlay.setAttribute('id', 'video-overlay');
      overlay.style.width = '100vw';
      overlay.style.height = '100vh';
      overlay.style.zIndex = '9999';
      overlay.style.position = 'fixed';
      overlay.style.top = '0';
      overlay.style.left = '0';

      const handler = async () => {
        const hasOverlay = document.getElementById('video-overlay');
        if (!hasOverlay) return;

        document.body.removeChild(overlay);
        overlay.removeEventListener('click', handler);
        overlay.removeEventListener('mouseup', handler);
        overlay.removeEventListener('mousemove', handler);
        overlay.removeEventListener('touchstart', handler);
        overlay.removeEventListener('touchmove', handler);

        this.hasPlayPermission = true;
        await this.video.play();
        // in case with iOS Safari browser, the video is muted at the moment (read the link below)
        // https://developer.apple.com/documentation/webkit/delivering_video_content_for_safari#3030259
        //
        // so, we need to unmute it and display the current state
        this.video.muted = false;
      };

      overlay.addEventListener('click', handler);
      overlay.addEventListener('mousedown', handler);
      overlay.addEventListener('mousemove', handler);
      overlay.addEventListener('touchstart', handler);
      overlay.addEventListener('touchmove', handler);
      document.body.append(overlay);
    } else {
      this.video.play().catch((e) => {
        console.error(e);
      });
    }
  }

  releaseTexture() {
    if (this.texture) {
      this.outputs.texture = null;
      this.outputs.loadingState = LoadingState.Idle;
      this.texture.dispose();
    }
    if (this.video) {
      this.video.pause();
      this.video.src = '';
    }
    if (this.plane) {
      this.plane.parent?.remove(this.plane);
      this.plane.material.dispose();
      this.plane.geometry.dispose();
      this.plane = null;
    }
    if (this.backSidePlane) {
      this.backSidePlane.parent?.remove(this.backSidePlane);
      this.backSidePlane.material.dispose();
      this.backSidePlane.geometry.dispose();
      this.backSidePlane = null;
    }
  }

  getSize = () => [this.inputs.width, this.aspectHeight];

  private createVideoElement() {
    const { muted, loop, autoplay, playsInline } = this.inputs;
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.autoplay = Boolean(autoplay);
    video.muted = Boolean(muted);
    video.loop = Boolean(loop);
    video.playsInline = Boolean(playsInline);
    return video;
  }

  // private makeRoundedRect = (x, y, width, height, radius) => {
  //   const THREE = this.context.three;
  //
  //   const roundedRectShape = new THREE.Shape();
  //   roundedRectShape.moveTo(x, y + radius);
  //   roundedRectShape.lineTo(x, y + height - radius);
  //   roundedRectShape.quadraticCurveTo(x, y + height, x + radius, y + height);
  //   roundedRectShape.lineTo(x + width - radius, y + height);
  //   roundedRectShape.quadraticCurveTo(x + width, y + height, x + width, y + height - radius);
  //   roundedRectShape.lineTo(x + width, y + radius);
  //   roundedRectShape.quadraticCurveTo(x + width, y, x + width - radius, y);
  //   roundedRectShape.lineTo(x + radius, y);
  //   roundedRectShape.quadraticCurveTo(x, y, x, y + radius);
  //
  //   return new THREE.ShapeGeometry(roundedRectShape);
  // };
}

export const videoRendererType = 'treedis.videoRenderer';

export function makeVideoRenderer() {
  return new VideoRenderer();
}
